import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Filter, Wand2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

const MATCH_TYPES = [
    { v: "contains", label: "Contains" },
    { v: "starts_with", label: "Starts with" },
    { v: "equals", label: "Equals" },
    { v: "regex", label: "Regex" },
];
const DIRECTIONS = [
    { v: "any", label: "Any" },
    { v: "deposit", label: "Deposit (credits in)" },
    { v: "withdrawal", label: "Withdrawal (debits out)" },
];

export default function BankRules() {
    const [rules, setRules] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const empty = { name: "", match_type: "contains", pattern: "", direction: "any", category: "", counterparty: "", account_id: "", priority: 100, enabled: true };
    const [form, setForm] = useState(empty);
    const [applying, setApplying] = useState(false);

    const load = async () => {
        const [r, a] = await Promise.all([api.get("/bank-rules"), api.get("/coa")]);
        setRules(r.data); setAccounts(a.data);
    };
    useEffect(() => { load(); }, []);

    const openEdit = (it) => { setForm(it ? { ...empty, ...it } : empty); setDrawer({ open: true, item: it }); };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, priority: Number(form.priority) || 100 };
            if (drawer.item) await api.put(`/bank-rules/${drawer.item.id}`, payload);
            else await api.post("/bank-rules", payload);
            toast.success(drawer.item ? "Rule updated" : "Rule created");
            setDrawer({ open: false, item: null });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this rule?")) return;
        await api.delete(`/bank-rules/${id}`);
        await load();
    };

    const toggle = async (rule) => {
        await api.put(`/bank-rules/${rule.id}`, { ...rule, enabled: !rule.enabled });
        await load();
    };

    const applyAll = async () => {
        setApplying(true);
        try {
            const { data } = await api.post("/bank-rules/apply", { only_uncategorized: true });
            toast.success(`Matched ${data.matched} of ${data.scanned} transactions across ${data.rules_used} rules`);
            await load();
        } finally { setApplying(false); }
    };

    return (
        <>
            <PageHeader
                kicker="BANKING"
                title="Bank Rules"
                subtitle="Auto-categorize bank transactions on import. First matching rule wins (lower priority = checked first)."
                actions={
                    <>
                        <Button variant="outline" onClick={applyAll} disabled={applying} data-testid="apply-rules-btn"><Wand2 className="w-4 h-4 mr-1.5" />{applying ? "Applying…" : "Apply to existing"}</Button>
                        <Button onClick={() => openEdit(null)} data-testid="rule-new"><Plus className="w-4 h-4 mr-1.5" />New rule</Button>
                    </>
                }
            />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {rules.length === 0 ? (
                        <div className="p-10 text-center text-muted-foreground">
                            <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No rules yet. Create rules to auto-tag bank transactions on import (e.g., "contains 'zomato' → Meals & Entertainment").</p>
                        </div>
                    ) : (
                        <table className="fin-table">
                            <thead><tr><th>Priority</th><th>Name</th><th>Pattern</th><th>Direction</th><th>Sets</th><th className="num">Hits</th><th></th></tr></thead>
                            <tbody>
                                {rules.map((r) => (
                                    <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                                        <td className="font-mono text-[12px]">{r.priority}</td>
                                        <td>
                                            <div className="font-medium">{r.name}</div>
                                            {!r.enabled && <span className="text-[10.5px] uppercase tracking-wide text-muted-foreground">disabled</span>}
                                        </td>
                                        <td>
                                            <span className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground mr-1.5">{r.match_type.replace("_", " ")}</span>
                                            <span className="font-mono text-[12px] bg-[#fafaf7] px-2 py-0.5 rounded">{r.pattern}</span>
                                        </td>
                                        <td className="text-[12.5px]">{(DIRECTIONS.find((d) => d.v === r.direction) || {}).label || r.direction}</td>
                                        <td className="text-[12.5px]">
                                            {r.category && <span className="block">📂 {r.category}</span>}
                                            {r.counterparty && <span className="block text-muted-foreground">👤 {r.counterparty}</span>}
                                        </td>
                                        <td className="num text-[12.5px] text-muted-foreground">{r.match_count || 0}</td>
                                        <td className="text-right whitespace-nowrap">
                                            <Button size="sm" variant="ghost" onClick={() => toggle(r)} title={r.enabled ? "Disable" : "Enable"}>
                                                {r.enabled ? <ToggleRight className="w-4 h-4 text-[#1f6c47]" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-3.5 h-3.5 text-[#b4233a]" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </PageBody>

            <Sheet open={drawer.open} onOpenChange={(v) => !v && setDrawer({ open: false, item: null })}>
                <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4">
                        <SheetTitle className="text-xl font-semibold tracking-tight">{drawer.item ? "Edit rule" : "New bank rule"}</SheetTitle>
                    </SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-3" onSubmit={save}>
                        <div><Label>Rule name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" placeholder="e.g. Categorize Zomato as Meals" data-testid="rule-name" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Match type</Label>
                                <Select value={form.match_type} onValueChange={(v) => setForm({ ...form, match_type: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{MATCH_TYPES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Direction</Label>
                                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Pattern (matched against transaction description)</Label>
                            <Input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} required className="mt-1 font-mono" placeholder={form.match_type === "regex" ? "^UPI-.*ZOMATO" : "zomato"} data-testid="rule-pattern" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1" /></div>
                            <div className="flex items-end pb-1">
                                <label className="flex items-center gap-2 text-[12.5px]">
                                    <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled
                                </label>
                            </div>
                        </div>
                        <div className="border-t border-border pt-3">
                            <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-muted-foreground mb-2">SET ON MATCH</p>
                            <div className="space-y-3">
                                <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Meals & Entertainment" className="mt-1" /></div>
                                <div><Label>Counterparty</Label><Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder="e.g. Zomato" className="mt-1" /></div>
                                <div>
                                    <Label>Suggest CoA Account (optional)</Label>
                                    <Select value={form.account_id || "none"} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v })}>
                                        <SelectTrigger className="mt-1"><SelectValue placeholder="No suggestion" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— None —</SelectItem>
                                            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} • {a.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <Button type="submit" className="w-full" data-testid="rule-save">{drawer.item ? "Update rule" : "Create rule"}</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}
